// Compressão de PDF no navegador (usada pela aba Manuais).
// Objetivo: aceitar PDFs de até 100MB no front e entregar ao servidor no máx. 40MB.
// TODO o processamento roda no navegador do usuário — o servidor não comprime.
//
// Estratégia (um único parse pdf-lib para os estágios 1-3):
//   1. Re-save (useObjectStreams) — segundos, PRESERVA texto.
//   2. DEDUPE de imagens repetidas: logos/carimbos que aparecem em todas as
//      páginas às vezes são embutidos UMA CÓPIA POR PÁGINA (Word/scanners).
//      Imagens com conteúdo idêntico (hash SHA-256) viram UM único objeto —
//      as referências das outras páginas são reescritas para ele e os
//      duplicados são removidos do arquivo.
//   3. RECOMPRESSÃO das imagens embutidas (JPEG/DCTDecode): decodifica com o
//      codec nativo, reduz dimensão + qualidade mirando ~100KB por imagem e
//      substitui no MESMO lugar. Cache por hash: cada imagem única é
//      processada UMA vez, mesmo que apareça em 600 páginas.
//      Texto, fontes, tabelas e vetores ficam intocados.
//   4. Rasterização via pdf.js (último recurso, raro): passe ÚNICO adaptativo
//      — a qualidade cai página a página conforme o orçamento aperta, sem
//      recomeçar do zero. PERDE a camada de texto — por isso o texto é
//      extraído ANTES (getTextContent) e devolvido em `text` para o servidor
//      usar nas chamadas de IA (com marcadores de página p/ citações).
//
// Cancelamento: passe um PdfCancelToken e sete cancelled=true para abortar
// (lança PdfCompressCancelled).

export interface PdfCompressProgress {
  stage: 'analisando' | 'otimizando' | 'imagens' | 'extraindo-texto' | 'comprimindo' | 'montando'
  page?: number
  pages?: number
}

export interface PdfCancelToken { cancelled: boolean }

export class PdfCompressCancelled extends Error {
  constructor() { super('Compressão cancelada.') }
}

export interface PdfCompressResult {
  blob: Blob
  wasCompressed: boolean
  rasterized: boolean
  /** Texto extraído com marcadores "[Página N]" — só preenchido quando rasterizado. */
  text: string
  pageCount: number
  originalBytes: number
  finalBytes: number
}

// ── Parâmetros de imagem (estágio 3) ──
const IMG_TARGET_BYTES = 100 * 1024 // alvo por imagem (~100KB)
const IMG_MAX_DIM = 1280            // dimensão máxima (px)
const IMG_QUALITY_LADDER = [0.6, 0.45, 0.32]

// ── Presets da rasterização (estágio 4) ──
interface RasterPreset { maxWidth: number; quality: number }
const RASTER_PRESETS: RasterPreset[] = [
  { maxWidth: 1600, quality: 0.72 },
  { maxWidth: 1300, quality: 0.6 },
  { maxWidth: 1050, quality: 0.5 },
  { maxWidth: 900, quality: 0.42 },
  { maxWidth: 750, quality: 0.35 }
]

function checkCancel(cancel?: PdfCancelToken) {
  if (cancel?.cancelled) throw new PdfCompressCancelled()
}

async function loadPdfJs() {
  const pdfjsLib = await import('pdfjs-dist')
  // Mesmo padrão do ProjectFilesSection: worker via CDN, confiável p/ uso pontual
  ;(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
    `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`
  return pdfjsLib as any
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Falha ao gerar imagem da página.'))),
      'image/jpeg',
      quality
    )
  })
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource)
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
}

// Reencoda um JPEG (bytes) mirando IMG_TARGET_BYTES. Retorna null se não valer
// a pena (falha de decode ou resultado maior que o original).
async function reencodeJpeg(
  jpegBytes: Uint8Array,
  allowResize: boolean
): Promise<{ bytes: Uint8Array; width: number; height: number } | null> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(new Blob([jpegBytes], { type: 'image/jpeg' }))
  } catch {
    return null // JPEG exótico (CMYK etc.) — mantém o original
  }
  try {
    const scale = allowResize ? Math.min(1, IMG_MAX_DIM / Math.max(bitmap.width, bitmap.height)) : 1
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(bitmap, 0, 0, w, h)
    let best: Blob | null = null
    for (const q of IMG_QUALITY_LADDER) {
      best = await canvasToJpegBlob(canvas, q)
      if (best.size <= IMG_TARGET_BYTES) break
    }
    if (!best) return null
    const bytes = new Uint8Array(await best.arrayBuffer())
    return bytes.length < jpegBytes.length ? { bytes, width: w, height: h } : null
  } finally {
    bitmap.close?.()
  }
}

interface ImageStreamInfo {
  ref: any
  stream: any
  hash: string
  isDct: boolean
  hasSMask: boolean
  dedupeKey: string
}

// Estágios 2 + 3 sobre um PDFDocument JÁ carregado:
// dedupe de imagens idênticas + recompressão das JPEG. Retorna true se mudou algo.
async function dedupeAndRecompressImages(
  pdfLib: any,
  doc: any,
  onProgress?: (p: PdfCompressProgress) => void,
  cancel?: PdfCancelToken
): Promise<boolean> {
  const { PDFName, PDFRawStream, PDFArray, PDFDict, PDFRef } = pdfLib
  const ctx = doc.context

  const nSubtype = PDFName.of('Subtype')
  const nImage = PDFName.of('Image')
  const nFilter = PDFName.of('Filter')
  const nDCT = PDFName.of('DCTDecode')
  const nSMask = PDFName.of('SMask')

  const filterOf = (dict: any): any => dict.get(nFilter)
  const isDctFilter = (filter: any): boolean => {
    if (filter === nDCT) return true
    if (filter instanceof PDFArray) {
      const arr = filter.asArray()
      return arr.length === 1 && arr[0] === nDCT
    }
    return false
  }

  // ── Coleta todas as imagens (qualquer filtro — dedupe vale p/ todas) ──
  const images: ImageStreamInfo[] = []
  for (const [ref, obj] of ctx.enumerateIndirectObjects()) {
    if (!(obj instanceof PDFRawStream)) continue
    if (obj.dict.get(nSubtype) !== nImage) continue
    checkCancel(cancel)
    const hash = await sha256Hex(obj.contents)
    const filter = filterOf(obj.dict)
    images.push({
      ref,
      stream: obj,
      hash,
      isDct: isDctFilter(filter),
      hasSMask: Boolean(obj.dict.get(nSMask)),
      // chave de dedupe: conteúdo + dict essencial (dois streams iguais com
      // dicts diferentes não podem ser mesclados)
      dedupeKey: hash + '|' + String(obj.dict.get(PDFName.of('Width'))) + '|' +
        String(obj.dict.get(PDFName.of('Height'))) + '|' + String(filter) + '|' +
        String(obj.dict.get(PDFName.of('ColorSpace'))) + '|' +
        String(obj.dict.get(PDFName.of('BitsPerComponent')))
    })
  }
  if (!images.length) return false

  let changed = false

  // ── DEDUPE: imagens idênticas viram um único objeto ──
  // (imagens com SMask ficam fora: a máscara é um objeto separado e mesclar
  //  referências poderia cruzar máscaras diferentes)
  const byKey = new Map<string, ImageStreamInfo[]>()
  for (const info of images) {
    if (info.hasSMask) continue
    const list = byKey.get(info.dedupeKey) || []
    list.push(info)
    byKey.set(info.dedupeKey, list)
  }
  const refReplacements = new Map<any, any>() // ref duplicado -> ref canônico
  for (const list of byKey.values()) {
    if (list.length < 2) continue
    const canonical = list[0]
    for (let i = 1; i < list.length; i++) refReplacements.set(list[i].ref, canonical.ref)
  }
  if (refReplacements.size > 0) {
    // Reescreve TODAS as referências aos duplicados (uma única varredura)
    const rewrite = (value: any): any => {
      if (value instanceof PDFRef) return refReplacements.get(value) || value
      return value
    }
    for (const [, obj] of ctx.enumerateIndirectObjects()) {
      if (obj instanceof PDFDict) {
        for (const [key, value] of obj.entries()) {
          const next = rewrite(value)
          if (next !== value) obj.set(key, next)
        }
      } else if (obj instanceof PDFArray) {
        for (let i = 0; i < obj.size(); i++) {
          const value = obj.get(i)
          const next = rewrite(value)
          if (next !== value) obj.set(i, next)
        }
      }
    }
    // Percorre também dicts/arrays ANINHADOS dentro dos objetos indiretos
    const visit = (node: any, depth: number) => {
      if (depth > 8) return
      if (node instanceof PDFDict) {
        for (const [key, value] of node.entries()) {
          const next = rewrite(value)
          if (next !== value) node.set(key, next)
          else visit(value, depth + 1)
        }
      } else if (node instanceof PDFArray) {
        for (let i = 0; i < node.size(); i++) {
          const value = node.get(i)
          const next = rewrite(value)
          if (next !== value) node.set(i, next)
          else visit(value, depth + 1)
        }
      } else if (node instanceof PDFRawStream) {
        visit(node.dict, depth + 1)
      }
    }
    for (const [, obj] of ctx.enumerateIndirectObjects()) visit(obj, 0)
    // Remove os objetos órfãos
    for (const dupRef of refReplacements.keys()) ctx.delete(dupRef)
    changed = true
  }

  // ── RECOMPRESSÃO das JPEG únicas restantes (cache por hash) ──
  const survivors = images.filter(info => !refReplacements.has(info.ref) && info.isDct)
  const candidates = survivors.filter(info => info.stream.contents.length > IMG_TARGET_BYTES)
  const reencodeCache = new Map<string, { bytes: Uint8Array; width: number; height: number } | null>()
  for (let i = 0; i < candidates.length; i++) {
    checkCancel(cancel)
    onProgress?.({ stage: 'imagens', page: i + 1, pages: candidates.length })
    const info = candidates[i]
    // Imagem com SMask: redimensionar quebraria a máscara — só reencoda no mesmo tamanho
    let result = reencodeCache.get(info.hash)
    if (result === undefined) {
      result = await reencodeJpeg(info.stream.contents, !info.hasSMask)
      reencodeCache.set(info.hash, result)
    }
    if (!result) continue
    const dictEntries: Record<string, unknown> = {
      Type: 'XObject',
      Subtype: 'Image',
      Width: result.width,
      Height: result.height,
      ColorSpace: 'DeviceRGB',
      BitsPerComponent: 8,
      Filter: 'DCTDecode'
    }
    const smask = info.stream.dict.get(nSMask)
    if (smask) dictEntries.SMask = smask
    ctx.assign(info.ref, ctx.stream(result.bytes, dictEntries))
    changed = true
  }

  return changed
}

// ── Estágio 4: rasterização em passe ÚNICO adaptativo ──
// A qualidade só desce (nunca volta a subir): cada página tem uma "franquia"
// proporcional ao orçamento restante; se estourar, o preset cai para as
// próximas páginas — sem recomeçar o documento do zero.
async function rasterAllPagesAdaptive(
  pdf: any,
  budgetBytes: number,
  onProgress?: (p: PdfCompressProgress) => void,
  cancel?: PdfCancelToken
): Promise<Array<{ jpeg: ArrayBuffer; widthPt: number; heightPt: number }> | null> {
  const pages: Array<{ jpeg: ArrayBuffer; widthPt: number; heightPt: number }> = []
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas indisponível neste navegador.')

  let presetIdx = 0
  let total = 0
  for (let i = 1; i <= pdf.numPages; i++) {
    checkCancel(cancel)
    onProgress?.({ stage: 'comprimindo', page: i, pages: pdf.numPages })
    const page = await pdf.getPage(i)
    const base = page.getViewport({ scale: 1 }) // scale 1 = pontos (72dpi)
    const remainingPages = pdf.numPages - i + 1
    const allowance = Math.max(18 * 1024, ((budgetBytes - total) / remainingPages) * 1.25)

    let blob: Blob | null = null
    // Renderiza no preset atual; se estourar a franquia da página, desce o
    // preset e tenta de novo (só para ESTA página em diante — sem recomeço)
    for (;;) {
      const preset = RASTER_PRESETS[presetIdx]
      const scale = Math.max(Math.min(preset.maxWidth / base.width, 2.5), 0.3)
      const viewport = page.getViewport({ scale })
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: ctx, viewport }).promise
      blob = await canvasToJpegBlob(canvas, preset.quality)
      if (blob.size <= allowance || presetIdx >= RASTER_PRESETS.length - 1) break
      presetIdx++
    }
    total += blob!.size
    pages.push({ jpeg: await blob!.arrayBuffer(), widthPt: base.width, heightPt: base.height })
    page.cleanup?.()
    if (total > budgetBytes) return null // nem no preset mínimo coube
  }
  return pages
}

async function extractTextWithPageMarkers(
  pdf: any,
  onProgress?: (p: PdfCompressProgress) => void,
  cancel?: PdfCancelToken
): Promise<string> {
  const parts: string[] = []
  for (let i = 1; i <= pdf.numPages; i++) {
    checkCancel(cancel)
    onProgress?.({ stage: 'extraindo-texto', page: i, pages: pdf.numPages })
    try {
      const page = await pdf.getPage(i)
      const content = await page.getTextContent()
      const text = content.items.map((it: any) => it.str || '').join(' ').replace(/\s+/g, ' ').trim()
      if (text) parts.push(`[Página ${i}]\n${text}`)
      page.cleanup?.()
    } catch {
      // página com problema não deve derrubar a extração inteira
    }
  }
  return parts.join('\n\n')
}

export async function compressPdfToTarget(
  file: File,
  targetBytes: number,
  onProgress?: (p: PdfCompressProgress) => void,
  cancel?: PdfCancelToken
): Promise<PdfCompressResult> {
  const originalBytes = file.size
  if (originalBytes <= targetBytes) {
    return { blob: file, wasCompressed: false, rasterized: false, text: '', pageCount: 0, originalBytes, finalBytes: originalBytes }
  }

  onProgress?.({ stage: 'analisando' })
  const sourceBuffer = await file.arrayBuffer()

  // ── Estágios 1-3: UM único parse do pdf-lib (parsear 2x um PDF de 60MB
  //    era o principal motivo de lentidão da versão anterior) ──
  try {
    const pdfLib = await import('pdf-lib')
    const { PDFDocument } = pdfLib as any
    const doc = await PDFDocument.load(sourceBuffer, { ignoreEncryption: true, updateMetadata: false })
    checkCancel(cancel)

    // Estágio 1: re-save simples
    onProgress?.({ stage: 'otimizando' })
    let out = await doc.save({ useObjectStreams: true })
    if (out.byteLength <= targetBytes) {
      return {
        blob: new Blob([out], { type: 'application/pdf' }),
        wasCompressed: true,
        rasterized: false,
        text: '',
        pageCount: doc.getPageCount(),
        originalBytes,
        finalBytes: out.byteLength
      }
    }

    // Estágios 2+3: dedupe + recompressão das imagens (no MESMO doc)
    const changed = await dedupeAndRecompressImages(pdfLib, doc, onProgress, cancel)
    if (changed) {
      onProgress?.({ stage: 'montando' })
      out = await doc.save({ useObjectStreams: true })
      if (out.byteLength <= targetBytes) {
        return {
          blob: new Blob([out], { type: 'application/pdf' }),
          wasCompressed: true,
          rasterized: false, // camada de texto preservada — servidor extrai normalmente
          text: '',
          pageCount: doc.getPageCount(),
          originalBytes,
          finalBytes: out.byteLength
        }
      }
    }
  } catch (e) {
    if (e instanceof PdfCompressCancelled) throw e
    // PDF que o pdf-lib não processa — segue para rasterização
  }

  // ── Estágio 4: rasterização adaptativa (LENTO; perde texto → extrai antes) ──
  checkCancel(cancel)
  const pdfjsLib = await loadPdfJs()
  const pdf = await pdfjsLib.getDocument({ data: sourceBuffer.slice(0) }).promise
  const pageCount: number = pdf.numPages

  try {
    const text = await extractTextWithPageMarkers(pdf, onProgress, cancel)

    // Orçamento com folga p/ estrutura do PDF final (~5%)
    const budget = Math.floor(targetBytes * 0.95)
    const pages = await rasterAllPagesAdaptive(pdf, budget, onProgress, cancel)
    if (!pages) {
      throw new Error(
        `Não foi possível comprimir este PDF para ${Math.round(targetBytes / 1024 / 1024)}MB. ` +
        'Ele provavelmente tem páginas demais ou imagens muito pesadas — divida o manual em partes menores.'
      )
    }

    onProgress?.({ stage: 'montando' })
    const { PDFDocument: PDFDocumentOut } = await import('pdf-lib')
    const out = await PDFDocumentOut.create()
    for (const pg of pages) {
      checkCancel(cancel)
      const img = await out.embedJpg(pg.jpeg)
      const page = out.addPage([pg.widthPt, pg.heightPt])
      page.drawImage(img, { x: 0, y: 0, width: pg.widthPt, height: pg.heightPt })
    }
    const outBytes = await out.save({ useObjectStreams: true })

    return {
      blob: new Blob([outBytes], { type: 'application/pdf' }),
      wasCompressed: true,
      rasterized: true,
      text,
      pageCount,
      originalBytes,
      finalBytes: outBytes.byteLength
    }
  } finally {
    pdf.destroy?.()
  }
}
